'use client';

import { PegValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: PegValues;
  onValuesChange: (field: keyof PegValues, value: number | string) => void;
  translations: ToolsTranslations['peg'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof PegValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof PegValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Price per Share */}
      <div className="space-y-3">
        <label htmlFor="price-per-share" className="block text-sm font-medium text-gray-700">
          {translations.pricePerShare}
        </label>
        <div className="relative">
          <input
            id="price-per-share"
            type="number"
            min="0"
            step="0.01"
            value={values.pricePerShare}
            onChange={(e) => handleInputChange('pricePerShare', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        <div className="relative">
          <input
            id="price-per-share-slider"
            type="range"
            min="1"
            max="1000"
            step="1"
            value={values.pricePerShare}
            onChange={(e) => handleSliderChange('pricePerShare', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.pricePerShare} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.pricePerShare / 1000) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>1000 {translations.currencySuffix}</span>
        </div>
      </div>

      {/* EPS Latest */}
      <div className="space-y-3">
        <label htmlFor="eps-latest" className="block text-sm font-medium text-gray-700">
          {translations.epsLatest}
        </label>
        <div className="relative">
          <input
            id="eps-latest"
            type="number"
            min="0"
            step="0.01"
            value={values.epsLatest}
            onChange={(e) => handleInputChange('epsLatest', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        <div className="relative">
          <input
            id="eps-latest-slider"
            type="range"
            min="1"
            max="100"
            step="1"
            value={values.epsLatest}
            onChange={(e) => handleSliderChange('epsLatest', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.epsLatest} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.epsLatest / 100) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>100 {translations.currencySuffix}</span>
        </div>
      </div>

      {/* EPS Previous */}
      <div className="space-y-3">
        <label htmlFor="eps-previous" className="block text-sm font-medium text-gray-700">
          {translations.epsPrevious}
        </label>
        <div className="relative">
          <input
            id="eps-previous"
            type="number"
            min="0"
            step="0.01"
            value={values.epsPrevious}
            onChange={(e) => handleInputChange('epsPrevious', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        <div className="relative">
          <input
            id="eps-previous-slider"
            type="range"
            min="1"
            max="100"
            step="1"
            value={values.epsPrevious}
            onChange={(e) => handleSliderChange('epsPrevious', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.epsPrevious} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.epsPrevious / 100) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>100 {translations.currencySuffix}</span>
        </div>
      </div>
    </div>
  );
}


